import { Box, Flex, Spinner, Text } from '@chakra-ui/react';
import { GIFS_SEARCH } from 'constants/mixpanel';
import { useDebouncedCallback } from 'hooks/useDebounce/useDebouncedCallback';
import { useGifsSearch } from 'hooks/useGifsSearch';
import { useSideMenu } from 'hooks/useSideMenu';
import { Mixpanel } from 'libs/third-party/Mixpanel/mixpanel';
import { SearchInput } from 'modules/common/components/SearchInput/SearchInput';
import { ReactElement, memo, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { RANDOM_TAG } from './GifsWidgetPack.config';
import { InfiniteList } from './List';
import { SuggestedTagList } from './SuggestedTagList';

export interface LottieWidgetMenuProps {
  isMenuActive: boolean;
  disableDragDrop?: boolean;
}

export const GifsWidgetPack = memo(({ isMenuActive, disableDragDrop }: LottieWidgetMenuProps): ReactElement | null => {
  const { data, query, setQuery, nextPage, isLoadingMore, clearSearch } = useGifsSearch();
  const [queryStr, setQueryStr] = useState('');
  const { t } = useTranslation('editor_widget_menu', { useSuspense: false, keyPrefix: 'sideMenu.gifsMenu' });
  const [defaultTag] = useState<string>(RANDOM_TAG);
  const hasSetDefaultTag = useRef<boolean>(false);

  const setDefaultSearch = () => {
    if (hasSetDefaultTag.current) return;
    onSearch(defaultTag, true);
    hasSetDefaultTag.current = true;
  };

  useSideMenu({ isMenuActive, onOpenMenu: setDefaultSearch });

  // since the there is no delay when searching on a json file this fix the scroll not going to top when a search changes
  // it can be removed when isLoading works
  const searchWithDebounce = useDebouncedCallback((searchValue: string, isFirstSearch: boolean = false) => {
    setQuery(searchValue);
    if (!isFirstSearch) {
      Mixpanel.track(GIFS_SEARCH.eventName, {
        query: searchValue,
      });
    }
  }, 200);

  const onSearch = useCallback(
    (searchValue: string, isFirstSearch: boolean = false) => {
      setQuery('');
      searchWithDebounce(searchValue, isFirstSearch);
    },
    [searchWithDebounce, setQuery],
  );

  const onClearSearch = () => {
    clearSearch();
    // reset back to default tag previously loaded
    onSearch(defaultTag);
  };

  const onSuggestedTagClick = useCallback(
    (searchValue: string) => {
      setQueryStr(searchValue);
      onSearch(searchValue);
    },
    [onSearch],
  );

  // auto load a default tag
  useEffect(() => {
    onSearch(defaultTag, true);
  }, [defaultTag, onSearch]);

  return (
    <Box h='full' w='full' p={1} display='block'>
      <Flex direction='column' h='full'>
        <Box pr='2' width='100%' display='flex' justifyContent='center'>
          <SearchInput
            value={queryStr}
            setValue={setQueryStr}
            onSearch={onSearch}
            clearSearch={onClearSearch}
            label={t('searchPlaceholder')}
            clearButtonLabel={t('clearButtonLabel')}
            focusOnMenuOpen={true}
            isVisible={isMenuActive}
          />
        </Box>
        {/* TODO Maybe we can reuse the photo widget */}
        <SuggestedTagList onTagClick={onSuggestedTagClick} />
        {query && !data.length && !isLoadingMore && <NoResultsFound query={query} />}
        {query && !!data.length && (
          <InfiniteList data={data} numberOfItems={data.length} loadMore={nextPage} disableDragDrop={disableDragDrop} />
        )}
        {query && isLoadingMore && (
          <Spinner data-testid='gifs-widgetmenu-spinner' alignSelf='center' color='brand.500' mt={4} />
        )}
      </Flex>
    </Box>
  );
});
GifsWidgetPack.displayName = 'GifsWidgetPack';

interface NoResultsFoundProps {
  query: string;
}

export const NoResultsFound = ({ query }: NoResultsFoundProps): ReactElement => {
  const { t } = useTranslation('editor_widget_menu', { useSuspense: false, keyPrefix: 'sideMenu.gifsMenu' });
  return <Text p='4'>{t('noResultsFound', { query })}</Text>;
};
